import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Usage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-bold">Usage Guide</h1>
      <p className="mt-4 text-slate-300">
        DraftLion lives inside your AI tool — Claude or ChatGPT. You don't click buttons in
        DraftLion; you talk to your AI like you'd talk to the sharpest fantasy
        buddy in your league, and DraftLion feeds it the data. Everything below is
        example phrasing you can copy, paste, and tweak.
      </p>
      <p className="mt-4 text-slate-400 text-sm">
        New here? Make sure you've finished the{' '}
        <Link to="/setup" className="text-amber-400 hover:text-amber-300">setup guide</Link>{' '}
        first — DraftLion needs to be wired into your AI tool before any of these
        prompts will do anything.
      </p>
      <br></br>
      <section className="max-w-5xl mx-auto px-6">
        {/* `overflow-hidden` clips the video to the container's rounded corners.
            `bg-black` shows behind any unavoidable letterboxing (object-cover
            should prevent it, but belt-and-suspenders). */}
        <div className="aspect-video rounded-xl overflow-hidden border border-slate-800 bg-black">
          <video
            poster="https://assets-939204158944-us-east-1-an.s3.us-east-1.amazonaws.com/The+Draft+Lion+Demo-Cover.jpg"
            className="w-full h-full object-cover"
            controls
            preload="metadata"
            playsInline
            title="DraftLion demo"
            src="https://www.youtube.com/watch?v=zLoBAIJHN1g"
          >
            Your browser does not support HTML5 video.
          </video>
        </div>
      </section>

      {/* ============================================================ */}
      {/* SLEEPER IDS                                                  */}
      {/* ============================================================ */}
      <Section title="What you'll need from Sleeper">
        <p>
          Most prompts work best when DraftLion knows three things: your{' '}
          <strong>Sleeper username</strong>, your <strong>league ID</strong>, and (during
          a draft) your <strong>draft ID</strong>. Grab these once and reuse them for
          the whole season.
        </p>

        {/* Username */}
        <h3 className="mt-8 text-lg font-semibold text-white">Your Sleeper username</h3>
        <p>This is the name you log in with. It's case-sensitive.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <SourceCard heading="On the website">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Go to <a className="text-amber-400 hover:text-amber-300" href="https://sleeper.com" target="_blank" rel="noopener">sleeper.com</a> and sign in.</li>
              <li>Click your avatar in the top-right.</li>
              <li>Your username sits at the top of the menu (the bit with the @ symbol next to your display name).</li>
            </ol>
          </SourceCard>
          <SourceCard heading="In the mobile app">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Open the Sleeper app.</li>
              <li>Tap the <strong>account</strong> tab (your avatar, bottom-right).</li>
              <li>Your username appears under your display name at the top of the screen.</li>
            </ol>
          </SourceCard>
        </div>

        {/* League ID */}
        <h3 className="mt-10 text-lg font-semibold text-white">Your league ID</h3>
        <p>
          A long number that uniquely identifies your league. You'll have one per
          league.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <SourceCard heading="On the website">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Go to <a className="text-amber-400 hover:text-amber-300" href="https://sleeper.com" target="_blank" rel="noopener">sleeper.com</a>, sign in, and click into your league.</li>
              <li>
                Look at the URL. It'll look like:
                <CodeBlock>https://sleeper.com/leagues/1234567890123456789/team</CodeBlock>
              </li>
              <li>
                The number between <Code>/leagues/</Code> and the next <Code>/</Code> is
                your league ID. Copy it.
              </li>
            </ol>
          </SourceCard>
          <SourceCard heading="In the mobile app">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Open your league.</li>
              <li>Tap the <strong>league</strong> tab at the bottom (looks like a trophy or shield).</li>
              <li>Tap the <strong>settings</strong> gear icon (top-right).</li>
              <li>Scroll down — the <strong>League ID</strong> is listed near the bottom. Tap it to copy.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">
              If the mobile app's UI has shifted and you can't find it, the easiest
              fallback is to open sleeper.com in your phone's browser and use the
              website method above.
            </p>
          </SourceCard>
        </div>

        {/* Draft ID */}
        <h3 className="mt-10 text-lg font-semibold text-white">Your draft ID (during draft season)</h3>
        <p>
          A separate ID for the draft itself. Each draft has its own ID, even
          mock drafts.
        </p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <SourceCard heading="On the website">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Open your draft on <a className="text-amber-400 hover:text-amber-300" href="https://sleeper.com" target="_blank" rel="noopener">sleeper.com</a>.</li>
              <li>
                The URL looks like:
                <CodeBlock>https://sleeper.com/draft/nfl/1234567890123456789</CodeBlock>
              </li>
              <li>
                The trailing number is your draft ID.
              </li>
            </ol>
          </SourceCard>
          <SourceCard heading="In the mobile app">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Open your league and tap the <strong>draft</strong> tile.</li>
              <li>Tap the menu icon (•••) in the top-right of the draft screen.</li>
              <li>Tap <strong>Share</strong> — the link includes the draft ID at the end.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">
              Easiest cross-device move: open the draft once on the website and grab
              the ID from the URL.
            </p>
          </SourceCard>
        </div>

        <Callout type="info">
          <strong>You only need to share these once per chat.</strong> After you give
          your AI tool your username and IDs at the start of a conversation, it
          remembers them for the rest of the chat. Subsequent prompts can be as
          short as "who should I draft?"
        </Callout>
      </Section>

      {/* ============================================================ */}
      {/* DRAFTING                                                     */}
      {/* ============================================================ */}
      <Section title="Using DraftLion during a draft">
        <p>
          Three steps: connect, ask, follow up. The whole flow lives inside your
          AI tool of choice — Claude or ChatGPT.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">1. Connect your AI to the draft</h3>
        <p>
          Open a fresh chat and paste this opener (swap in your real values):
        </p>
        <CodeBlock>{`I'm using DraftLion. My Sleeper username is yourSleeperName and my
draft ID is 1234567890123456789. Can you connect to the draft and
confirm you can see my roster?`}</CodeBlock>
        <p className="text-sm text-slate-400">
          Your AI will call DraftLion behind the scenes, look you up, and confirm the
          league format (PPR, superflex, etc.) and how many picks have been made. If
          anything looks wrong, fix it now before your first pick is on the clock.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">2. Ask for a pick</h3>
        <p>When you're on the clock, ask in plain English:</p>
        <CodeBlock>Who should I draft?</CodeBlock>
        <p>
          You'll get a ranked list with names, positions, and the reasoning behind
          each suggestion (positional need, value over replacement, drop-off at the
          position, injury status, etc.).
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">3. Useful follow-up prompts</h3>
        <p>Some prompts that come up a lot mid-draft:</p>
        <CodeBlock>Show me only RBs available right now.</CodeBlock>
        <CodeBlock>What does my roster look like? What's my biggest need?</CodeBlock>
        <CodeBlock>Who's trending up in the last 24 hours?</CodeBlock>
        <CodeBlock>Is Player X or Player Y the better pick here given my roster?</CodeBlock>
        <CodeBlock>What picks have happened since my last turn?</CodeBlock>

        <Callout type="info">
          <strong>You can interrupt and ask "why?"</strong> If a recommendation looks
          weird, ask your AI to explain. It can show you the score breakdown — VORP,
          need bonus, drop-off, etc. — so you understand what's driving the pick
          before you commit.
        </Callout>

        <Callout type="warning">
          <strong>Run a 5-minute test the day before your draft.</strong> Open your AI
          tool, point it at any draft you have access to (a mock works fine), and
          ask for a recommendation. This catches every common setup issue while you
          have time to fix it instead of with a 30-second pick clock running.
        </Callout>
      </Section>

      {/* ============================================================ */}
      {/* TRADES                                                       */}
      {/* ============================================================ */}
      <Section title="Evaluating trades">
        <p>
          DraftLion compares the value of both sides of a trade using daily-updated
          consensus values, then weighs that against your actual roster needs. Works
          for redraft and dynasty leagues, with players, picks, or both.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">1. Basic player-for-player trade</h3>
        <p>
          Just describe the trade. Your AI will pull values for both sides and tell
          you which side wins (and by how much):
        </p>
        <CodeBlock>{`I'm in league 1234567890123456789 as yourSleeperName. Someone offered
me Justin Jefferson for Bijan Robinson and Garrett Wilson. Is this a
good trade for me?`}</CodeBlock>
        <p className="text-sm text-slate-400">
          DraftLion returns the per-player values, the totals on each side, the net
          difference, and a verdict that factors in your current roster needs — so a
          "fair" trade can still be a yes for you (or a no) based on what your team
          actually needs.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">2. Dynasty trades with picks</h3>
        <p>
          For dynasty leagues, DraftLion can value future draft picks too. Mention
          it's dynasty so it pulls the right values, and describe picks the way
          you'd say them out loud:
        </p>
        <CodeBlock>{`This is a dynasty league. I'm being offered the 2026 1.05 and a 2027
2nd for my Breece Hall. Worth it?`}</CodeBlock>
        <p className="text-sm text-slate-400">
          Specific picks like "2026 1.05" get exact values. Vaguer picks like
          "2027 2nd" get a tiered estimate (Early/Mid/Late). If a player or pick
          isn't in the dataset, your AI will tell you and treat it as unknown
          rather than guessing.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-white">3. Comparing multiple offers</h3>
        <p>
          You can throw a few offers at your AI at once and ask it to rank them:
        </p>
        <CodeBlock>{`I have three trade offers for CeeDee Lamb. Which is the best for my
team?

  A: Saquon Barkley + Jaylen Waddle
  B: Drake London + Rachaad White + 2026 2nd
  C: Puka Nacua + Jonathan Taylor`}</CodeBlock>

        <Callout type="info">
          <strong>Always include the league ID and your username.</strong> Trade
          analysis is much sharper when DraftLion can pull your current roster and
          league format. Without those, it'll still give you a value comparison but
          can't factor in need.
        </Callout>
      </Section>

      {/* ============================================================ */}
      {/* TIPS                                                         */}
      {/* ============================================================ */}
      <Section title="Tips for getting the best results">
        <ul className="space-y-3">
          <Tip>
            <strong>Front-load context once.</strong> Give your league ID, draft ID,
            and Sleeper username at the start of the chat. After that, your AI
            remembers them and you can just ask short questions.
          </Tip>
          <Tip>
            <strong>Be specific about format if it's unusual.</strong> DraftLion
            auto-detects scoring (PPR / Half / Standard) and superflex from your
            league settings, but if you're in a TE-premium or 0.5-PPR-with-bonuses
            league, mention it. Your AI can adjust the framing.
          </Tip>
          <Tip>
            <strong>Ask "why" liberally.</strong> The recommendation tools return
            reasoning alongside the score. If you don't see it, ask your AI to show
            the reasoning — it's there.
          </Tip>
          <Tip>
            <strong>Use it after the draft too.</strong> Your subscription covers
            the whole NFL season. Run trade evals during the season, check trending
            adds before the waiver run, scout your bench.
          </Tip>
          <Tip>
            <strong>Mention DraftLion by name if your AI ignores it.</strong> If
            ChatGPT or Claude responds without using DraftLion, prompt: "Use
            DraftLion to…" — that nudges it to call the connector.
          </Tip>
        </ul>
      </Section>

      {/* ============================================================ */}
      {/* TROUBLESHOOTING                                              */}
      {/* ============================================================ */}
      <Section title="Common questions">
        <Faq q="My AI says it can't find my draft or league">
          Double-check the ID. Sleeper draft IDs and league IDs are different
          numbers — the draft ID is in the URL when you're inside a draft, the
          league ID is in the URL when you're on the league home page. Also confirm
          the draft is started (DraftLion can connect to drafts in "drafting" or
          "complete" status, but not ones that haven't kicked off yet).
        </Faq>
        <Faq q="A player I asked about returns no value">
          A handful of deep-bench players, rookies who haven't been added to
          consensus rankings yet, or recently-cut/retired guys may not be in the
          daily value sync. Your AI will flag this and treat them as unknown rather
          than guessing. Usually means the trade is so lopsided the missing player
          doesn't matter — but you can always ask for a gut-check the
          old-fashioned way.
        </Faq>
        <Faq q="Recommendations seem off — am I doing something wrong?">
          Probably not. The two most common causes: (1) you didn't share your
          username, so DraftLion can't see your roster and is recommending in a
          vacuum, or (2) the league format detection got it wrong. Ask your AI to
          confirm the format it's using; if it's wrong, tell it the correct one and
          re-ask.
        </Faq>
        <Faq q="Can I use this for mock drafts?">
          Yes — any draft you have access to in Sleeper works the same way. Mocks
          are actually the best place to get familiar with DraftLion before your
          real draft.
        </Faq>
        <Faq q="None of this is working — help">
          Email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">
            support@thedraftlion.com
          </a>{' '}
          with your league ID, what you asked your AI, and what came back. We reply
          within 24 hours, faster during draft season.
        </Faq>
      </Section>

      <p className="mt-16 text-center text-slate-400">
        That's all there is to it. Now go win your league. 🏆
      </p>
    </div>
  );
}

// ------------------------------------------------------------------
// UI primitives (mirrors Setup.tsx so look-and-feel stays identical)
// ------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12 pt-12 border-t border-slate-800 first:border-0 first:pt-0 first:mt-12">
      <h2 className="text-xl md:text-2xl font-bold text-white">{title}</h2>
      <div className="mt-4 space-y-4 text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available */ }
  }
  return (
    <div className="relative my-4">
      <pre className="code-block whitespace-pre-wrap break-all">{children}</pre>
      <button
        onClick={copy}
        className="absolute top-3 right-3 text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <code className="font-mono text-sm bg-slate-800 px-1.5 py-0.5 rounded">{children}</code>;
}

function Callout({ type, children }: { type: 'warning' | 'info'; children: React.ReactNode }) {
  const ring = type === 'warning' ? 'border-amber-500/50 bg-amber-500/5' : 'border-blue-500/50 bg-blue-500/5';
  return (
    <div className={`my-6 rounded-lg border ${ring} p-4 text-sm`}>{children}</div>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-semibold text-white">{q}</h3>
      <div className="mt-1 text-sm text-slate-400">{children}</div>
    </div>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-amber-400 mt-1" aria-hidden>✓</span>
      <span>{children}</span>
    </li>
  );
}

function SourceCard({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 text-sm text-slate-300">
      <h4 className="font-semibold text-white mb-2">{heading}</h4>
      {children}
    </div>
  );
}
